import { FunctionComponent, useState } from 'react'
import { useDebouncedCallback } from '@mantine/hooks'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { BaseInputElement } from '~/components/Input'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Popover, PopoverContentWrapper, PopoverLine } from '~/components/Popover'
import { Tooltip } from '~/components/Tooltip'

const SEARCH_DEBOUNCE_TIME = 300

/**
 * The width lives on the wrapper, not on the input: the toolbar spreads its children apart, so an
 * unwrapped input is only ever as wide as its placeholder.
 */
const SEARCH_CLASSES = 'w-2/5 max-w-[592px] shrink-0'

type Props = {
  search: string
  onSearchChange: (search: string) => void
  /** Only the rule kinds that record one pass this — the button is absent otherwise, not disabled. */
  onOpenHistory?: () => void
  historyTrackingLabel?: TrackingLabel
  createLabel: string
  onCreate: () => void
  createTrackingLabel: TrackingLabel
  canCreate: boolean
  /** Shown on the create button's tooltip when `canCreate` is false. */
  createDisabledReason?: string
  /** Owner-only, like the archived list itself. False hides the menu entry rather than disabling it. */
  canSeeArchived: boolean
  archivedLabel: string
  onNavigateToArchived: () => void
  archivedTrackingLabel: TrackingLabel
  actionsTrackingLabel: TrackingLabel
}

/**
 * The action row every rule list carries, in one place: search / spacer / history / new / more.
 *
 * The four rule lists had four different rows — one with search and history, one with neither, one
 * with the create button up beside the breadcrumbs — so moving between them meant finding the same
 * four controls somewhere else each time. The order is the one the medias and ad units lists already
 * read in, which is what makes this consistent with the rest of the app rather than only with itself.
 *
 * Search is debounced because it is written into the URL; the filtering itself is instant, since these
 * lists are already in the browser in full.
 */
export const RuleListToolbar: FunctionComponent<Props> = ({
  search,
  onSearchChange,
  onOpenHistory,
  historyTrackingLabel,
  createLabel,
  onCreate,
  createTrackingLabel,
  canCreate,
  createDisabledReason,
  canSeeArchived,
  archivedLabel,
  onNavigateToArchived,
  archivedTrackingLabel,
  actionsTrackingLabel,
}) => {
  const [actionsOpen, setActionsOpen] = useState(false)
  const [localSearch, setLocalSearch] = useState(search)
  // Resync from the prop ("previous value" pattern — adjust during render, not in an effect), so a
  // cleared query or a back navigation is reflected in the box.
  const [previousSearch, setPreviousSearch] = useState(search)
  if (search !== previousSearch) {
    setPreviousSearch(search)
    setLocalSearch(search)
  }

  const debouncedOnSearchChange = useDebouncedCallback(onSearchChange, SEARCH_DEBOUNCE_TIME)

  return (
    <div className={'my-3 flex w-full items-center justify-between gap-3'}>
      <div className={SEARCH_CLASSES}>
        <BaseInputElement
          type={'text'}
          iconLeft={'search'}
          labelIsPlaceholder
          label={'Search'}
          value={localSearch}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setLocalSearch(event.target.value)
            debouncedOnSearchChange(event.target.value)
          }}
        />
      </div>
      <div className={'flex items-center gap-3'}>
        {onOpenHistory && historyTrackingLabel && (
          <Tooltip title={'History'}>
            <Button iconName={'time'} variant={'tertiary'} onClick={onOpenHistory} trackingLabel={historyTrackingLabel} />
          </Tooltip>
        )}
        {/* Creating a rule is an owner's: a rule is shared, so a new one is a thing the whole scope can
            reach for. The button stays on screen saying why — seeing that the capability exists is half
            of knowing to ask an owner for it. */}
        <Tooltip title={createDisabledReason} disabled={canCreate} align={'end'}>
          <Button variant={'primary'} disabled={!canCreate} onClick={onCreate} trackingLabel={createTrackingLabel}>
            {createLabel}
          </Button>
        </Tooltip>
        {/* The acts that are not filtering, behind one control — the same menu the medias and ad units
            lists put their archive entry in. The archived list is the only entry today, so the trigger
            itself is owner-only: a menu that opens onto nothing is worse than no menu. */}
        {canSeeArchived && (
          <Popover
            align={'end'}
            open={actionsOpen}
            setOpen={setActionsOpen}
            trigger={<Button variant={'tertiary'} iconName={'more_vert'} title={'More actions'} trackingLabel={actionsTrackingLabel} />}
          >
            <PopoverContentWrapper>
              <PopoverLine
                onClick={() => {
                  setActionsOpen(false)
                  onNavigateToArchived()
                }}
                data-tracking-label={archivedTrackingLabel}
              >
                <Icon width={'20px'} name={'archive'} />
                <span>{archivedLabel}</span>
              </PopoverLine>
            </PopoverContentWrapper>
          </Popover>
        )}
      </div>
    </div>
  )
}
